import React from "react";
import "./TeamfightGrid.css";

const GRID_SIZE = 20; // Standard grid size
const TILE_SIZE_PX = 20; // 20px tiles
const CHAMPION_SIZE = 2; // Champions occupy 2x2 tiles (40px x 40px)

type Team = "BLUE" | "RED";
type Role = "TOP" | "JGL" | "MID" | "ADC" | "SUP";

type Unit = {
  id: string;
  team: Team;
  role: Role;
  x: number;
  y: number;
  range: number;
  health: number;
  maxHealth: number;
};

type Effect =
  | { id: number; kind: "aoe"; x: number; y: number; radius: number; team: Team; duration: number }
  | { id: number; kind: "beam"; x1: number; y1: number; x2: number; y2: number; team: Team; duration: number };

interface TeamfightGridProps {
  units: Unit[];
  selectedId: string | null;
  targetId: string | null;
  showRanges: boolean;
  showCoords: boolean;
  effects: Effect[];
  hover: { x: number; y: number } | null;
  invalidFlashAt: { x: number; y: number } | null;
  inRangeTiles: Set<string>;
  onLeftClickTile: (x: number, y: number) => void;
  onRightClickTile: (e: React.MouseEvent, x: number, y: number) => void;
  onHover: (x: number, y: number) => void;
}

const TEAM_BG: Record<Team, string> = { BLUE: "#3B82F6", RED: "#EF4444" };

const keyXY = (x: number, y: number) => `${x},${y}`;
const roleShort = (role: Role) => (role === "JGL" ? "JG" : role);

function UnitChip({ unit, selected, target }: { unit: Unit; selected: boolean; target?: boolean }) {
  const healthPercent = (unit.health / unit.maxHealth) * 100;
  const isDead = unit.health <= 0;
  
  return (
    <div
      className={`tf-unit-chip ${selected ? "selected" : ""} ${target ? "target" : ""} ${isDead ? "dead" : ""}`}
      data-team={unit.team}
      title={`${unit.team} ${unit.role} - ${unit.health}/${unit.maxHealth} HP (range ${unit.range})`}
      style={{ background: isDead ? "#666" : TEAM_BG[unit.team] }}
    >
      <div className="tf-unit-chip__content">
        {roleShort(unit.role)}
      </div>
      <div className="tf-unit-chip__health-bar">
        <div 
          className="tf-unit-chip__health-fill" 
          style={{ 
            width: `${healthPercent}%`,
            backgroundColor: healthPercent > 60 ? "#22c55e" : healthPercent > 30 ? "#eab308" : "#ef4444"
          }} 
        />
      </div>
    </div>
  );
}

function Beam({ x1, y1, x2, y2, color, duration }: { x1: number; y1: number; x2: number; y2: number; color: string; duration: number }) {
  const dx = (x2 - x1) * TILE_SIZE_PX;
  const dy = (y2 - y1) * TILE_SIZE_PX;
  const len = Math.hypot(dx, dy);
  const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
  const left = x1 * TILE_SIZE_PX + (TILE_SIZE_PX * CHAMPION_SIZE) / 2; // Center of 2x2 champion
  const top = y1 * TILE_SIZE_PX + (TILE_SIZE_PX * CHAMPION_SIZE) / 2;

  return (
    <div
      className="tf-effect-beam"
      style={{
        left,
        top,
        width: len,
        transform: `rotate(${angle}deg)`,
        background: color,
        animationDuration: `${duration}ms`,
      }}
    />
  );
}

export default function TeamfightGrid({
  units,
  selectedId,
  targetId,
  showRanges,
  showCoords,
  effects,
  hover,
  invalidFlashAt,
  inRangeTiles,
  onLeftClickTile,
  onRightClickTile,
  onHover,
}: TeamfightGridProps) {
  // Create occupancy map for 2x2 champion footprints
  const occ = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const u of units) {
      for (let dx = 0; dx < CHAMPION_SIZE; dx++) {
        for (let dy = 0; dy < CHAMPION_SIZE; dy++) {
          m.set(keyXY(u.x + dx, u.y + dy), u.id);
        }
      }
    }
    return m;
  }, [units]);

  const selectedUnit = units.find((u) => u.id === selectedId) || null;
  const targetUnit = units.find((u) => u.id === targetId) || null;

  return (
    <div className="tf-grid-wrap">
      {/* Effects layer */}
      <div className="tf-effects">
        {effects.map((ef) =>
          ef.kind === "aoe" ? (
            <div
              key={ef.id}
              className="tf-effect-aoe"
              style={{
                left: ef.x * TILE_SIZE_PX + (TILE_SIZE_PX * CHAMPION_SIZE) / 2,
                top: ef.y * TILE_SIZE_PX + (TILE_SIZE_PX * CHAMPION_SIZE) / 2,
                width: (ef.radius! * 2) * TILE_SIZE_PX,
                height: (ef.radius! * 2) * TILE_SIZE_PX,
                borderColor: ef.team === "BLUE" ? TEAM_BG.BLUE : TEAM_BG.RED,
                animationDuration: `${ef.duration}ms`,
              }}
            />
          ) : (
            <Beam
              key={ef.id}
              x1={ef.x1}
              y1={ef.y1}
              x2={ef.x2}
              y2={ef.y2}
              color={ef.team === "BLUE" ? TEAM_BG.BLUE : TEAM_BG.RED}
              duration={ef.duration}
            />
          )
        )}
      </div>

      <div className="tf-grid" onContextMenu={(e) => e.preventDefault()}>
        {Array.from({ length: GRID_SIZE * GRID_SIZE }).map((_, idx) => {
          const x = idx % GRID_SIZE;
          const y = Math.floor(idx / GRID_SIZE);
          const k = keyXY(x, y);
          const occupiedBy = occ.get(k);
          const inRange = inRangeTiles.has(k);
          const isHover = hover && hover.x === x && hover.y === y;
          const isHoverArea = hover && 
            x >= hover.x && x < hover.x + CHAMPION_SIZE && 
            y >= hover.y && y < hover.y + CHAMPION_SIZE;
          const isTarget = targetUnit && 
            x >= targetUnit.x && x < targetUnit.x + CHAMPION_SIZE && 
            y >= targetUnit.y && y < targetUnit.y + CHAMPION_SIZE;
          const invalid =
            invalidFlashAt && invalidFlashAt.x === x && invalidFlashAt.y === y;

          return (
            <button
              key={k}
              onClick={() => onLeftClickTile(x, y)}
              onContextMenu={(e) => onRightClickTile(e, x, y)}
              onMouseEnter={() => onHover(x, y)}
              className={`tf-tile ${inRange ? "tf-tile--inrange" : ""} ${isTarget ? "tf-tile--target" : ""} ${isHoverArea ? "tf-tile--hover" : ""} ${invalid ? "tf-tile--invalid" : ""}`}
              aria-label={`tile ${x},${y}`}
            >
              {showCoords && <span className="tf-coord">{x},{y}</span>}
              {isHoverArea && <span className="tf-hover-cursor" />}
              {occupiedBy && (() => {
                const unit = units.find((u) => u.id === occupiedBy)!;
                // Only show the unit chip on the top-left corner of their 2x2 footprint
                if (x === unit.x && y === unit.y) {
                  return (
                    <UnitChip 
                      unit={unit} 
                      selected={selectedId === occupiedBy} 
                      target={targetId === occupiedBy} 
                    />
                  );
                }
                return null;
              })()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export { GRID_SIZE, TILE_SIZE_PX, CHAMPION_SIZE };
export type { Unit, Effect, Team, Role };
